import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useClub } from '@/contexts/ClubContext';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { History, Trophy, Calendar as CalendarIcon, Clock } from 'lucide-react';
import StarRating from '@/components/StarRating';
import { PaginationControls } from '@/components/ui/pagination-controls';
import PageSpinner from '@/components/PageSpinner';
import { PageHeaderSkeleton, TableSkeleton } from '@/components/PageSkeletons';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Checkbox } from '@/components/ui/checkbox';
import { billingService, feedbackService, memberService, matchService } from '@/services/storageService';
import { getLatestEvaluation, mapEvaluationsFromApi, mapMatchHistoryFromApi, mapMembersFromApi } from '@/lib/clubApiMappers';
import type { ApiClubMember } from '@/types';

const formatDate = (timestamp?: string) => {
  if (!timestamp) return { date: '--', time: '--' };
  const date = new Date(timestamp);
  return {
    date: date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
    time: date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
  };
};

const renderStars = (count?: number) => {
  const safe = count === undefined || count === null || Number.isNaN(count) ? 0 : count;
  return <StarRating value={safe} size={11} />;
};

const getMemberRating = (member: any) => {
  if (!member) return undefined;
  const hasEvaluation =
    member.role === 'coach' || member.hasEvaluation || (member.evaluations && member.evaluations.length > 0);
  if (!hasEvaluation) return undefined;

  const latest = getLatestEvaluation(member.evaluations || []);
  return member.role === 'coach' ? 5 : latest?.rating;
};

export default function MatchHistory() {
  const { user } = useAuth();
  const { activeClubId } = useClub();
  const [currentPage, setCurrentPage] = React.useState(1);
  const [selectedDate, setSelectedDate] = React.useState<Date | null>(null);
  const [selectedPlayerIds, setSelectedPlayerIds] = React.useState<string[]>([]);
  const pageSize = 10;
  const clubId = activeClubId ? Number(activeClubId) : null;

  const membersQuery = useQuery({
    queryKey: ['club-members', clubId],
    queryFn: () => memberService.list(clubId as number) as Promise<ApiClubMember[]>,
    enabled: Boolean(clubId),
  });
  const billingQuery = useQuery({
    queryKey: ['billing-status', clubId],
    queryFn: () => billingService.getStatus(clubId),
    enabled: Boolean(user && clubId),
  });
  const resolvedPackageCode = String(
    billingQuery.data?.package || billingQuery.data?.recommended_package || 'package1',
  )
    .trim()
    .toLowerCase();
  const isEvaluationsFeatureAvailable = resolvedPackageCode !== 'package1';

  const memberRoleForUser = React.useMemo(() => {
    if (!user) return null;
    const list = (membersQuery.data || []) as ApiClubMember[];
    const match = list.find(
      (m) =>
        String(m.member_id) === String(user.id) ||
        (m.email && user.email && m.email.toLowerCase() === user.email.toLowerCase()),
    );
    return match?.role ? String(match.role).toLowerCase() : null;
  }, [membersQuery.data, user]);
  const roleForAccess = memberRoleForUser || user?.role || null;
  const isAdminOrCoach = roleForAccess === 'admin' || roleForAccess === 'coach';

  const historyQuery = useQuery({
    queryKey: ['match-history', clubId, user?.id, roleForAccess],
    queryFn: () => {
      if (!clubId || !user?.id) return [];
      if (isAdminOrCoach) return matchService.listHistoryByClub(clubId);
      return matchService.listHistoryForUserInClub(user.id, clubId);
    },
    enabled: Boolean(clubId && user?.id && membersQuery.isSuccess),
  });
  const evalsQuery = useQuery({
    queryKey: ['club-evals', clubId],
    queryFn: () => feedbackService.listForClub(clubId as number),
    enabled: Boolean(clubId) && isEvaluationsFeatureAvailable,
  });

  const baseMembers = React.useMemo(
    () => mapMembersFromApi((membersQuery.data || []) as ApiClubMember[]),
    [membersQuery.data],
  );
  const evalsByMember = React.useMemo(() => {
    const grouped: Record<string, any[]> = {};
    (evalsQuery.data || []).forEach((ev: any) => {
      const id = String(ev.player_id ?? ev.playerId ?? '');
      if (!id) return;
      if (!grouped[id]) grouped[id] = [];
      grouped[id].push(ev);
    });
    return grouped;
  }, [evalsQuery.data]);
  const members = React.useMemo(
    () =>
      baseMembers.map((m) => ({
        ...m,
        evaluations: isEvaluationsFeatureAvailable ? mapEvaluationsFromApi(evalsByMember[m.id] || []) : [],
        hasEvaluation: isEvaluationsFeatureAvailable
          ? m.hasEvaluation || (evalsByMember[m.id] || []).length > 0
          : false,
      })),
    [baseMembers, evalsByMember, isEvaluationsFeatureAvailable],
  );
  const matchHistory = React.useMemo(
    () => mapMatchHistoryFromApi(historyQuery.data as any[], members),
    [historyQuery.data, members],
  );

  const getMemberById = (id: string) => members.find((m) => m.id === id || m.userId === id);

  const isUserInMatch = (match: any) => {
    if (!user) return false;
    const participantIds = [...(match.team1 || []), ...(match.team2 || []), ...(match.players || [])];
    return participantIds.some((pid: string) => {
      const player = getMemberById(String(pid));
      const matchesMemberId = player && String(player.id) === String(user.id);
      const matchesUserId = player?.userId && String(player.userId) === String(user.id);
      return String(pid) === String(user.id) || matchesMemberId || matchesUserId;
    });
  };

  const filteredHistory = isAdminOrCoach ? matchHistory : matchHistory.filter(isUserInMatch);
  const playerOptions = React.useMemo(
    () =>
      members
        .map((m) => ({
          id: String(m.id),
          label: `${m.firstName} ${m.lastName}`.trim(),
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [members],
  );
  const selectedPlayerSet = React.useMemo(() => new Set(selectedPlayerIds), [selectedPlayerIds]);
  const matchesBySelectedPlayers = React.useMemo(() => {
    if (selectedPlayerIds.length === 0) return filteredHistory;
    return filteredHistory.filter((match: any) => {
      const ids = new Set(
        [...(match.team1 || []), ...(match.team2 || []), ...(match.players || [])].map((id: any) => String(id)),
      );
      if (isAdminOrCoach) {
        return selectedPlayerIds.some((id) => ids.has(String(id)));
      }
      // For members, only show matches they played with/against selected players
      return selectedPlayerIds.some((id) => ids.has(String(id)));
    });
  }, [filteredHistory, isAdminOrCoach, selectedPlayerIds]);
  const completedHistory = React.useMemo(
    () =>
      matchesBySelectedPlayers.filter((match: any) => match.status === 'finished' || Boolean(match.finishedAt)),
    [matchesBySelectedPlayers],
  );
  const matchesOnDate = React.useMemo(() => {
    if (!selectedDate) return completedHistory;
    const target = selectedDate.toDateString();
    return completedHistory.filter((match: any) => {
      const stamp = match.finishedAt || match.timestamp || match.startedAt;
      if (!stamp) return false;
      return new Date(stamp).toDateString() === target;
    });
  }, [completedHistory, selectedDate]);
  const total = matchesOnDate.length;

  const sortedHistory = React.useMemo(() => {
    const toTime = (match: any) => {
      const finish = match.finishedAt || match.timestamp;
      const start = match.startedAt || match.timestamp;
      return new Date(finish || start || 0).getTime();
    };
    return [...matchesOnDate].sort((a, b) => toTime(b) - toTime(a));
  }, [matchesOnDate]);

  const paged = sortedHistory.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  React.useEffect(() => {
    setCurrentPage(1);
  }, [total, selectedDate, selectedPlayerIds]);

  if (!clubId) {
    return <PageSpinner label="Select a club to view match history." />;
  }

  if (membersQuery.isLoading || historyQuery.isLoading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <PageHeaderSkeleton />
        <TableSkeleton rows={8} />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col gap-1">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-2xl sm:text-3xl font-display font-bold text-foreground flex items-center gap-3">
          <History className="text-primary shrink-0" />
          <span className="truncate">Match History</span>
        </h1>
          <div className="flex flex-col sm:flex-row gap-2 sm:items-center sm:flex-1 sm:justify-end">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="justify-start gap-2">
                  <CalendarIcon size={16} className="text-muted-foreground" />
                  {selectedDate
                    ? selectedDate.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' })
                    : 'All dates'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="p-0" align="end">
                <Calendar
                  mode="single"
                  selected={selectedDate ?? undefined}
                  onSelect={(date) => setSelectedDate(date ?? null)}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="justify-start gap-2">
                  Players
                  {selectedPlayerIds.length > 0 ? ` (${selectedPlayerIds.length})` : ''}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="p-0" align="end">
                <Command>
                  <CommandInput placeholder="Search players..." />
                  <CommandList>
                    <CommandEmpty>No players found.</CommandEmpty>
                    <CommandGroup>
                      {playerOptions.map((player) => (
                        <CommandItem
                          key={player.id}
                          value={player.label}
                          onSelect={() => {
                            setSelectedPlayerIds((prev) =>
                              prev.includes(player.id)
                                ? prev.filter((id) => id !== player.id)
                                : [...prev, player.id],
                            );
                          }}
                        >
                          <Checkbox checked={selectedPlayerSet.has(player.id)} className="mr-2" />
                          <span>{player.label}</span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
                <div className="p-2 border-t flex justify-end">
                  <Button size="sm" variant="ghost" onClick={() => setSelectedPlayerIds([])}>
                    Clear players
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
            <Button
              variant="ghost"
              onClick={() => {
                setSelectedDate(null);
                setSelectedPlayerIds([]);
              }}
            >
              Clear filters
            </Button>
          </div>
        </div>
        <p className="text-muted-foreground text-sm sm:text-base">
      {isAdminOrCoach
        ? `View all completed matches (${total} shown)`
        : `View your completed matches (${total} shown)`}
    </p>
      </div>

      {total > 0 ? (
        <div className="space-y-4">
          {paged.map((match: any) => {
            const startedAt = match.startedAt || match.timestamp;
            const finishedAt = match.finishedAt || null;
            const isFinished = match.status === 'finished' || Boolean(finishedAt);
            const startFmt = formatDate(startedAt);
            const finishFmt = isFinished && finishedAt ? formatDate(finishedAt) : null;
            const fallbackNameMap = match.playerNames || {};
            const formatPersonName = (person: any) =>
              `${person?.firstName || ''} ${person?.lastName || ''}`.trim();

            const resolvePlayer = (pid: string) => getMemberById(pid) || (fallbackNameMap as any)[String(pid)];
            const finisher = isFinished
              ? getMemberById(match.recordedBy) ||
              (fallbackNameMap as any)[String(match.recordedBy)] ||
              (match.recordedByName
                ? {
                  id: match.recordedBy || 'recorder',
                  firstName: match.recordedByName.split(' ')[0],
                  lastName: match.recordedByName.split(' ').slice(1).join(' '),
                  role: match.recordedByRole || undefined,
                }
                : null)
              : null;

            const assignerId = match.assignedByMemberId || match.assignedById;
            let assigner =
              assignerId && (getMemberById(assignerId) || (fallbackNameMap as any)[String(assignerId)]);
            if (!assigner && match.assignedByName) {
              assigner = {
                id: assignerId ? String(assignerId) : 'assigner',
                firstName: match.assignedByName.split(' ')[0],
                lastName: match.assignedByName.split(' ').slice(1).join(' '),
                role: match.assignedByRole || undefined,
              };
            }

            const team1Players = match.team1
              .map((id: string) => {
                const player = resolvePlayer(id);
                return player ? { id: String(id), ...player } : null;
              })
              .filter(Boolean) as any[];

            const team2Players = match.team2
              .map((id: string) => {
                const player = resolvePlayer(id);
                return player ? { id: String(id), ...player } : null;
              })
              .filter(Boolean) as any[];

            const isOngoing = !isFinished && (match.status === 'ongoing' || match.isActive);

            const winner = isFinished
              ? match.score.team1 > match.score.team2
                ? 'team1'
                : match.score.team2 > match.score.team1
                  ? 'team2'
                  : 'tie'
              : null;

            const Team = ({ players, isWinner }: { players: any[]; isWinner: boolean }) => (
              <div className={`flex flex-col gap-2 ${isWinner ? 'opacity-100' : 'opacity-80'}`}>
                {players.map((player) => (
                  <div key={player.id} className="flex items-center gap-2 min-w-0">
                    <Avatar className="h-8 w-8 shrink-0">
                      {player.avatarUrl && (
                        <AvatarImage
                          src={player.avatarUrl}
                          alt={player.avatarName || `${player.firstName} ${player.lastName}`}
                        />
                      )}
                      <AvatarFallback>
                        {(player.firstName?.[0] || '') + (player.lastName?.[0] || '')}
                      </AvatarFallback>
                    </Avatar>

                    <div className="min-w-0 flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium truncate max-w-[180px] sm:max-w-[240px]">
                        {player.firstName} {((player.lastName || '')[0] || '')}.
                      </span>
                      {renderStars(getMemberRating(player))}

                      {player.role === 'coach' && (
                        <span className="text-[11px] px-2 py-0.5 rounded-full bg-blue-100 text-blue-800">
                          Coach
                        </span>
                      )}
                      {player.role === 'admin' && (
                        <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">
                          Admin
                        </span>
                      )}
                    </div>
                  </div>
                ))}

                {isWinner && (
                  <span className="inline-block w-fit text-xs bg-success text-success-foreground px-2 py-0.5 rounded-full mt-1">
                    Winner
                  </span>
                )}
              </div>
            );

            const equipmentEntries = (match.equipmentUsed || [])
              .filter((eq: any) => eq.shoes || eq.racket)
              .map((eq: any) => {
                const player = resolvePlayer(String(eq.memberId));
                const name = player ? formatPersonName(player) : `Player ${eq.memberId}`;
                const pieces: { label: string; value: string }[] = [];
                if (eq.shoes) pieces.push({ label: 'Shoes', value: String(eq.shoes) });
                if (eq.racket) pieces.push({ label: 'Racket', value: String(eq.racket) });
                return { id: eq.memberId, name, pieces };
              });

            return (
              <Card key={match.id} className="overflow-hidden">
                <CardContent className="p-0">
                  <div className="flex flex-col md:flex-row">
                    {/* Left: Date/Time/Court (Desktop) */}
                    <div className="hidden md:flex p-4 bg-muted/50 md:w-56 flex-col gap-2">
                      <div className="flex items-center gap-2">
                        <CalendarIcon size={16} className="text-muted-foreground" />
                        <span className="text-sm font-medium">{startFmt.date}</span>
                      </div>

                      <div className="flex items-center gap-2">
                        <Clock size={16} className="text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">{startFmt.time}</span>
                      </div>

                      <div className="mt-2 flex items-center gap-2">
                        <Trophy size={14} className="text-primary" />
                        <span className="text-sm">Court {match.courtNumber}</span>
                      </div>

                      {/* ✅ NOTE UNDER COURT NUMBER */}
                      {match.notes?.trim() && (
                        <div className="mt-2 rounded-md bg-primary/10 px-2 py-1">
                          <span className="text-[11px] font-semibold text-primary">Note: </span>
                          <span className="text-[11px] text-foreground leading-snug break-words">
                            {match.notes.trim()}
                          </span>
                        </div>
                      )}

                      {isOngoing && (
                        <span className="mt-2 w-fit text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                          Ongoing
                        </span>
                      )}
                    </div>

                    {/* Mobile Top Bar */}
                    <div className="md:hidden px-4 py-3 bg-muted/50 flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <CalendarIcon size={16} className="text-muted-foreground shrink-0" />
                          <span className="text-sm font-medium">{startFmt.date}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <Clock size={14} className="text-muted-foreground shrink-0" />
                          <span className="text-xs text-muted-foreground">{startFmt.time}</span>
                        </div>
                      </div>

                      <div className="text-right shrink-0 flex flex-col items-end gap-1">
                        <div className="flex items-center gap-2">
                          <Trophy size={14} className="text-primary" />
                          <span className="text-sm">Court {match.courtNumber}</span>
                        </div>
                        {isOngoing && (
                          <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                            Ongoing
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Match Details */}
                    <div className="flex-1 p-4">
                      {/* Main layout: mobile stacked, desktop centered row */}
                      <div className="flex flex-col md:flex-row md:items-center md:justify-center gap-4 md:gap-6">
                        {/* Team 1 */}
                        <div className="md:flex-1 md:text-center">
                          <Team players={team1Players} isWinner={winner === 'team1'} />
                        </div>

                        {/* Score */}
                        <div className="flex items-center justify-center gap-3 py-1">
                          <span
                            className={`font-display font-bold ${winner === 'team1' ? 'text-success' : 'text-foreground'
                              } text-3xl sm:text-4xl`}
                          >
                            {match.score.team1}
                          </span>
                          <span className="text-xl sm:text-2xl text-muted-foreground">-</span>
                          <span
                            className={`font-display font-bold ${winner === 'team2' ? 'text-success' : 'text-foreground'
                              } text-3xl sm:text-4xl`}
                          >
                            {match.score.team2}
                          </span>
                        </div>

                        {/* Team 2 */}
                        <div className="md:flex-1 md:text-center">
                          <Team players={team2Players} isWinner={winner === 'team2'} />
                        </div>
                      </div>

                      {/* Timeline + recorder + extras */}
                      {(match.notes ||
                        match.equipmentUsed?.some((e: any) => e.shoes || e.racket) ||
                        finisher ||
                        assigner ||
                        finishFmt) && (
                          <div className="mt-4 pt-3 border-t text-xs sm:text-sm text-muted-foreground space-y-2">
                            <div className="flex flex-wrap gap-2">
                              <span className="inline-flex items-center gap-2 rounded-lg border bg-muted/30 px-3 py-1.5">
                                <span className="font-medium text-foreground">Started</span>
                                <span className="text-muted-foreground">
                                  {startFmt.date} • {startFmt.time}
                                </span>
                              </span>

                              <span className="inline-flex items-center gap-2 rounded-lg border bg-muted/30 px-3 py-1.5">
                                <span className="font-medium text-foreground">Finished</span>
                                <span className="text-muted-foreground">
                                  {finishFmt ? `${finishFmt.date} • ${finishFmt.time}` : '--'}
                                </span>
                              </span>

                              <span className="inline-flex items-center gap-2 rounded-lg border bg-muted/30 px-3 py-1.5">
                                <span className="font-medium text-foreground">Assigned by</span>
                                <span className="text-muted-foreground">
                                  {assigner ? formatPersonName(assigner) : assignerId ? `ID ${assignerId}` : '--'}
                                  {assigner?.role ? ` • ${assigner.role}` : ''}
                                </span>
                              </span>

                              <span className="inline-flex items-center gap-2 rounded-lg border bg-muted/30 px-3 py-1.5">
                                <span className="font-medium text-foreground">Finished by</span>
                                <span className="text-muted-foreground">
                                  {isFinished && finisher
                                    ? formatPersonName(finisher) || finisher.role || '--'
                                    : '--'}
                                  {isFinished && finisher?.role ? ` • ${finisher.role}` : ''}
                                </span>
                              </span>
                            </div>

                            {equipmentEntries.length > 0 && (
                              <div className="space-y-2">
                                <p className="text-xs uppercase tracking-wide text-muted-foreground">
                                  Equipment Used
                                </p>

                                <div className="grid gap-2 sm:grid-cols-2">
                                  {equipmentEntries.map((entry) => (
                                    <div
                                      key={`${match.id}-${entry.id}`}
                                      className="rounded-lg border bg-muted/30 px-3 py-2 flex flex-row gap-1"
                                    >
                                      <span className="text-sm font-medium text-foreground truncate">
                                        {entry.name}
                                      </span>

                                      <div className="flex flex-wrap gap-2">
                                        {entry.pieces.map((piece) => (
                                          <span
                                            key={piece.label}
                                            className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary px-2 py-1 text-xs"
                                          >
                                            <span className="font-semibold">{piece.label}</span>
                                            <span className="text-foreground">{piece.value}</span>
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <History className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No matches played yet. Start playing to see match history!</p>
          </CardContent>
        </Card>
      )}

      {total > pageSize && (
        <PaginationControls
          page={currentPage}
          pageSize={pageSize}
          total={total}
          onPageChange={setCurrentPage}
          className="mt-4"
        />
      )}
    </div>
  );
}
